import { useState, useRef, useEffect } from 'react';
import { Volume2, Play, Pause, Repeat, SkipBack, SkipForward } from 'lucide-react';
import { ReverseIcon } from './ReverseIcon';
import { SpeedIcon } from './SpeedIcon';
import './AudioPlayer.css';

interface AudioPlayerProps {
  audioUrl: string;
  filename?: string;
  currentTime?: number;
  onSeek?: (time: number) => void;
  onLoopToggle?: (isLooping: boolean) => void;
  onLoopRegionChange?: (region: { start: number; end: number } | null) => void;
  onReverseToggle?: (isReversed: boolean) => void;
  onSpeedChange?: (speed: number) => void;
  onSpectrumData?: (data: Uint8Array) => void;
  tempo?: number;
  /**
   * When this number changes, the player seeks to `currentTime` and starts
   * playback. Lets the parent trigger "click section → play from there"
   * without exposing the audio element.
   */
  playSignal?: number;
}

export function AudioPlayer({
  audioUrl,
  filename,
  currentTime: externalCurrentTime,
  onSeek: externalOnSeek,
  onLoopToggle,
  onLoopRegionChange,
  onReverseToggle,
  onSpeedChange,
  onSpectrumData,
  tempo,
  playSignal,
}: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceNodeRef = useRef<AudioBufferSourceNode | null>(null);
  const audioBufferRef = useRef<AudioBuffer | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const reverseIntervalRef = useRef<number | null>(null);
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isLooping, setIsLooping] = useState(false);
  const [loopStart, setLoopStart] = useState<number | null>(null);
  const [loopEnd, setLoopEnd] = useState<number | null>(null);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [isReversed, setIsReversed] = useState(false);
  const [isLoadingBuffer, setIsLoadingBuffer] = useState(false);

  // Initialize Web Audio API for spectrum analyzer (only after user interaction)
  useEffect(() => {
    if (!onSpectrumData || !isPlaying) return; // Only initialize when playing
    
    const audio = audioRef.current;
    if (!audio || !audioUrl) return;

    let mounted = true;

    const initWebAudio = async () => {
      try {
        // Only initialize if not already initialized
        if (audioContextRef.current && analyserRef.current) {
          // Start spectrum loop if already initialized
          const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
          const updateSpectrum = () => {
            if (!mounted || !analyserRef.current || !isPlaying) return;
            analyserRef.current.getByteFrequencyData(dataArray);
            onSpectrumData(dataArray);
            animationFrameRef.current = requestAnimationFrame(updateSpectrum);
          };
          updateSpectrum();
          return;
        }

        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioContextClass) return;

        const audioContext = new AudioContextClass();
        
        // Resume if suspended (required after user gesture)
        if (audioContext.state === 'suspended') {
          try {
            await audioContext.resume();
          } catch (err) {
            return;
          }
        }

        if (!mounted) return;

        try {
          const source = audioContext.createMediaElementSource(audio);
          const analyser = audioContext.createAnalyser();
          analyser.fftSize = 2048;
          analyser.smoothingTimeConstant = 0.8;
          
          source.connect(analyser);
          analyser.connect(audioContext.destination);
          
          audioContextRef.current = audioContext;
          analyserRef.current = analyser;

          // Start spectrum loop
          const dataArray = new Uint8Array(analyser.frequencyBinCount);
          const updateSpectrum = () => {
            if (!mounted || !analyserRef.current || !isPlaying) return;
            analyserRef.current.getByteFrequencyData(dataArray);
            onSpectrumData(dataArray);
            animationFrameRef.current = requestAnimationFrame(updateSpectrum);
          };
          updateSpectrum();
        } catch (err) {
          // CORS issue - continue without Web Audio API
        }
      } catch (error) {
        // Silently fail
      }
    };

    // Initialize Web Audio API when user starts playing
    initWebAudio();

    return () => {
      mounted = false;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isPlaying, onSpectrumData, audioUrl]);

  // Basic audio controls
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => {
      if (!isReversed) {
        const time = audio.currentTime;
        setCurrentTime(time);
        
        // DJ-style loop: check if we've reached loop end point
        if (isLooping && loopStart !== null && loopEnd !== null && time >= loopEnd) {
          audio.currentTime = loopStart;
          // Ensure playback continues
          if (!isPlaying) {
            audio.play().catch(err => {
              console.error('Failed to restart loop:', err);
            });
          }
        }
      }
    };
    const updateDuration = () => {
      if (!isNaN(audio.duration)) {
        setDuration(audio.duration);
      }
    };
    const handleEnded = () => {
      if (isLooping && !isReversed && loopStart !== null && loopEnd !== null) {
        // DJ-style loop: restart from loop start point
        audio.currentTime = loopStart;
        audio.play().catch(err => {
          console.error('Failed to restart loop:', err);
          setIsPlaying(false);
        });
      } else if (isLooping && !isReversed) {
        // Full track loop (fallback)
        audio.currentTime = 0;
        audio.play().catch(err => {
          console.error('Failed to restart loop:', err);
          setIsPlaying(false);
        });
      } else {
        setIsPlaying(false);
      }
    };
    const handleError = (e: any) => {
      console.error('Audio error:', e);
      setIsPlaying(false);
    };

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('durationchange', updateDuration);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('durationchange', updateDuration);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, [isLooping, isReversed, loopStart, loopEnd, isPlaying]);

  // Set loop property - disable HTML5 loop for DJ-style region looping
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      // Always disable HTML5 loop attribute - we handle looping manually for DJ-style
      audio.loop = false;
    }
  }, [isLooping, isReversed, isPlaying]);

  // Sync with external currentTime if provided
  useEffect(() => {
    if (externalCurrentTime !== undefined && audioRef.current && !isReversed) {
      const audio = audioRef.current;
      if (Math.abs(audio.currentTime - externalCurrentTime) > 0.1) {
        audio.currentTime = externalCurrentTime;
        setCurrentTime(externalCurrentTime);
      }
    }
  }, [externalCurrentTime, isReversed]);

  // playSignal — when it changes, seek to externalCurrentTime AND start playback.
  // Used by the "click a section to play it" flow.
  useEffect(() => {
    if (playSignal === undefined) return;
    const audio = audioRef.current;
    if (!audio || isReversed) return;
    if (externalCurrentTime !== undefined) {
      audio.currentTime = externalCurrentTime;
      setCurrentTime(externalCurrentTime);
    }
    audio
      .play()
      .then(() => {
        // Sync the play/pause button — without this the icon stays as ▶︎.
        setIsPlaying(true);
      })
      .catch(() => {
        // Autoplay can be blocked before any user interaction; ignore quietly.
      });
  }, [playSignal]);

  // Set playback speed
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      if (isReversed && sourceNodeRef.current) {
        // For reverse playback, we'll handle speed in the reverse logic
        // For now, just update the HTML5 audio rate when not reversed
      } else {
        audio.playbackRate = playbackSpeed;
      }
    }
  }, [playbackSpeed, isReversed]);

  const togglePlay = async () => {
    if (isReversed) {
      // Handle reverse playback pause/play
      if (sourceNodeRef.current) {
        if (isPlaying) {
          sourceNodeRef.current.stop();
          sourceNodeRef.current = null;
          setIsPlaying(false);
        } else {
          // Restart reverse playback
          await toggleReverse();
          await toggleReverse(); // Toggle twice to restart
        }
      }
      return;
    }

    const audio = audioRef.current;
    if (!audio) return;

    try {
      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
      } else {
        await audio.play();
        setIsPlaying(true);
      }
    } catch (err: any) {
      console.error('Error playing audio:', err);
      setIsPlaying(false);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;

    const newTime = parseFloat(e.target.value);
    audio.currentTime = newTime;
    setCurrentTime(newTime);

    if (externalOnSeek) {
      externalOnSeek(newTime);
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;

    const newVolume = parseFloat(e.target.value);
    audio.volume = newVolume;
    setVolume(newVolume);
  };

  const setLoopRegion = (bars: 4 | 8) => {
    const audio = audioRef.current;
    if (!audio || !duration || isReversed) return;
    
    // Calculate bar length based on BPM (assuming 4/4 time signature)
    // Formula: bar duration = (60 / BPM) * 4 beats per bar
    // For 4 bars: (60 / BPM) * 4 * 4 = (60 / BPM) * 16
    // For 8 bars: (60 / BPM) * 4 * 8 = (60 / BPM) * 32
    let barLength: number;
    if (tempo && tempo > 0) {
      const secondsPerBeat = 60 / tempo;
      const secondsPerBar = secondsPerBeat * 4; // 4 beats per bar (4/4 time)
      barLength = secondsPerBar * bars;
    } else {
      // Fallback: estimate based on common BPM ranges
      // Assume ~120 BPM average: (60/120) * 4 = 2 seconds per bar
      barLength = bars === 8 ? 16 : 8; // 8 bars = 16s, 4 bars = 8s
    }
    
    // Read live audio.currentTime — the React `currentTime` state lags up to
    // a frame behind the actual playhead, which made the waveform's IN
    // marker drift visibly from where the user clicked.
    const start = Math.max(0, audio.currentTime);
    const end = Math.min(duration, start + barLength);

    setLoopStart(start);
    setLoopEnd(end);
    setIsLooping(true);

    if (isPlaying && audio.currentTime >= end) {
      audio.currentTime = start;
    }

    if (onLoopToggle) onLoopToggle(true);
    if (onLoopRegionChange) onLoopRegionChange({ start, end });
  };

  const toggleLoop = () => {
    if (isReversed) return; // Don't allow loop when reversed
    
    const newLoopState = !isLooping;
    setIsLooping(newLoopState);
    
    if (!newLoopState) {
      setLoopStart(null);
      setLoopEnd(null);
      if (onLoopRegionChange) onLoopRegionChange(null);
    } else if (loopStart === null || loopEnd === null) {
      const audio = audioRef.current;
      if (audio && duration) {
        setLoopRegion(8);
      }
    } else if (onLoopRegionChange) {
      onLoopRegionChange({ start: loopStart, end: loopEnd });
    }

    if (onLoopToggle) onLoopToggle(newLoopState);
  };

  const clearLoop = () => {
    setIsLooping(false);
    setLoopStart(null);
    setLoopEnd(null);
    if (onLoopToggle) onLoopToggle(false);
    if (onLoopRegionChange) onLoopRegionChange(null);
  };

  // Load audio buffer for reverse playback
  const loadAudioBuffer = async (): Promise<AudioBuffer | null> => {
    if (audioBufferRef.current) {
      return audioBufferRef.current;
    }

    try {
      setIsLoadingBuffer(true);
      const audioContext = audioContextRef.current || new (window.AudioContext || (window as any).webkitAudioContext)();
      
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      const response = await fetch(audioUrl);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      audioBufferRef.current = audioBuffer;
      if (!audioContextRef.current) {
        audioContextRef.current = audioContext;
      }
      
      return audioBuffer;
    } catch (error) {
      console.error('Failed to load audio buffer for reverse:', error);
      return null;
    } finally {
      setIsLoadingBuffer(false);
    }
  };

  const toggleReverse = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    const newReverseState = !isReversed;
    
    if (newReverseState) {
      // Enable reverse playback
      const wasPlaying = isPlaying;
      
      // Pause HTML5 audio
      if (wasPlaying) {
        audio.pause();
      }

      // Load buffer if needed
      const buffer = await loadAudioBuffer();
      if (!buffer) {
        console.error('Could not load audio buffer for reverse playback');
        return;
      }

      // Start reverse playback using Web Audio API
      const audioContext = audioContextRef.current!;
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      // Create reversed buffer
      const reversedBuffer = audioContext.createBuffer(
        buffer.numberOfChannels,
        buffer.length,
        buffer.sampleRate
      );

      for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
        const channelData = buffer.getChannelData(channel);
        const reversedData = reversedBuffer.getChannelData(channel);
        for (let i = 0; i < channelData.length; i++) {
          reversedData[i] = channelData[channelData.length - 1 - i];
        }
      }

      // Calculate start position in reversed buffer (from current time)
      const normalDuration = buffer.duration;
      const reversedStartTime = normalDuration - currentTime;

      // Create source and play
      const source = audioContext.createBufferSource();
      source.buffer = reversedBuffer;
      source.playbackRate.value = playbackSpeed;
      
      const gainNode = audioContext.createGain();
      gainNode.gain.value = volume;
      
      source.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      sourceNodeRef.current = source;
      
      // Track reverse playback time
      const startTime = audioContext.currentTime;
      const duration = reversedBuffer.duration;
      
      source.onended = () => {
        setIsPlaying(false);
        setIsReversed(false);
        sourceNodeRef.current = null;
        if (reverseIntervalRef.current) {
          clearTimeout(reverseIntervalRef.current);
          reverseIntervalRef.current = null;
        }
      };

      source.start(0, Math.max(0, reversedStartTime));
      
      if (wasPlaying) {
        setIsPlaying(true);
      }
      
      // Update reverse time
      const updateReverseTime = () => {
        if (sourceNodeRef.current) {
          const elapsed = audioContext.currentTime - startTime;
          const reverseTime = Math.max(0, duration - elapsed - reversedStartTime);
          setCurrentTime(reverseTime);
          
          if (reverseTime > 0 && sourceNodeRef.current) {
            reverseIntervalRef.current = window.setTimeout(updateReverseTime, 50);
          } else {
            reverseIntervalRef.current = null;
            setIsPlaying(false);
            setIsReversed(false);
          }
        }
      };
      
      if (wasPlaying) {
        updateReverseTime();
      }
      
    } else {
      // Disable reverse playback, return to HTML5 audio
      if (sourceNodeRef.current) {
        try {
          sourceNodeRef.current.stop();
        } catch (e) {
          // Already stopped
        }
        sourceNodeRef.current.disconnect();
        sourceNodeRef.current = null;
      }
      
      if (reverseIntervalRef.current) {
        clearTimeout(reverseIntervalRef.current);
        reverseIntervalRef.current = null;
      }
      
      // Resume HTML5 audio at current position
      audio.currentTime = currentTime;
      if (isPlaying) {
        try {
          await audio.play();
        } catch (e) {
          console.error('Error resuming audio:', e);
        }
      }
    }
    
    setIsReversed(newReverseState);
    
    if (onReverseToggle) {
      onReverseToggle(newReverseState);
    }
  };

  const handleSpeedChange = (speed: number) => {
    setPlaybackSpeed(speed);
    
    if (onSpeedChange) {
      onSpeedChange(speed);
    }
  };

  const skipBy = (delta: number) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const next = Math.max(0, Math.min(duration, audio.currentTime + delta));
    audio.currentTime = next;
    setCurrentTime(next);
    if (externalOnSeek) externalOnSeek(next);
  };

  const formatTime = (seconds: number): string => {
    if (isNaN(seconds) || seconds < 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="audio-player">
      <audio 
        ref={audioRef} 
        src={audioUrl} 
        preload="metadata"
        crossOrigin="anonymous"
        onError={(e) => {
          console.error('Audio load error:', e);
          console.error('Audio URL:', audioUrl);
        }}
      />
      
      {filename && (
        <div className="player-filename">{filename}</div>
      )}

      <div className="player-controls">
        <button
          className="skip-button"
          onClick={() => skipBy(-10)}
          aria-label="Skip back 10 seconds"
          title="Skip back 10s"
        >
          <SkipBack size={14} strokeWidth={2.4} />
        </button>
        <button className="play-button" onClick={togglePlay} aria-label={isPlaying ? 'Pause' : 'Play'}>
          {isPlaying ? <Pause size={16} strokeWidth={2.4} /> : <Play size={16} strokeWidth={2.4} />}
        </button>
        <button
          className="skip-button"
          onClick={() => skipBy(10)}
          aria-label="Skip forward 10 seconds"
          title="Skip forward 10s"
        >
          <SkipForward size={14} strokeWidth={2.4} />
        </button>

        <div className="player-seek">
          <span className="time-current">{formatTime(currentTime)}</span>
          <input
            type="range"
            min="0"
            max={duration || 0}
            value={currentTime}
            onChange={handleSeek}
            className="seek-slider"
          />
          <span className="time-duration">{formatTime(duration)}</span>
        </div>

        <div className="player-volume">
          <span className="volume-icon" aria-hidden="true">
            <Volume2 size={14} strokeWidth={1.8} />
          </span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={handleVolumeChange}
            className="volume-slider"
            aria-label="Volume"
          />
        </div>
      </div>

      <div className="player-advanced-controls">
        <div className="loop-controls">
          <button
            className={`control-button icon-button ${isLooping ? 'active' : ''}`}
            onClick={toggleLoop}
            title="Toggle Loop"
            disabled={isReversed}
          >
            <Repeat size={16} strokeWidth={2.2} />
            <span>Loop</span>
          </button>
          {isLooping && (
            <div className="loop-buttons">
              <button
                className="loop-bar-button"
                onClick={() => setLoopRegion(4)}
                title="Set 4-bar loop from current position"
              >
                4 bars
              </button>
              <button
                className="loop-bar-button"
                onClick={() => setLoopRegion(8)}
                title="Set 8-bar loop from current position"
              >
                8 bars
              </button>
              <button
                className="loop-clear-button"
                onClick={clearLoop}
                title="Clear loop"
              >
                Clear
              </button>
            </div>
          )}
          {isLooping && loopStart !== null && loopEnd !== null && (
            <div className="loop-info">
              Loop: {formatTime(loopStart)} - {formatTime(loopEnd)} ({formatTime(loopEnd - loopStart)})
            </div>
          )}
        </div>
        {/* Direction toggle — Forward and Reverse as a mutually exclusive pair.
            Forward is the default state; clicking either flips to it. */}
        <div className="direction-toggle" role="radiogroup" aria-label="Playback direction">
          <button
            className={`control-button icon-button ${!isReversed ? 'active' : ''}`}
            onClick={() => { if (isReversed) toggleReverse(); }}
            title="Play forward"
            role="radio"
            aria-checked={!isReversed}
            disabled={isLoadingBuffer}
          >
            <Play size={16} strokeWidth={2.2} />
            <span>Forward</span>
          </button>
          <button
            className={`control-button icon-button ${isReversed ? 'active' : ''}`}
            onClick={() => { if (!isReversed) toggleReverse(); }}
            title="Play reverse"
            role="radio"
            aria-checked={isReversed}
            disabled={isLoadingBuffer}
          >
            <ReverseIcon size={18} active={isReversed} />
            <span>Reverse</span>
          </button>
        </div>
        <div className="playback-speed-control">
          <SpeedIcon size={16} />
          <label htmlFor="playback-speed">Speed</label>
          <select 
            id="playback-speed" 
            className="speed-select"
            value={playbackSpeed} 
            onChange={(e) => handleSpeedChange(parseFloat(e.target.value))}
            disabled={isReversed}
          >
            <option value="0.5">0.5x</option>
            <option value="0.75">0.75x</option>
            <option value="1">1x</option>
            <option value="1.25">1.25x</option>
            <option value="1.5">1.5x</option>
            <option value="2">2x</option>
          </select>
        </div>
      </div>
    </div>
  );
}
